<!--
  Interfacing the "bootstrap" ".card" structural classes to create a more
  emphasized element (filled background, light font). Not using "bootstrap-vue-next"
  <b-card> due to the automatic addition of an <b-card-body> inside (thus
  leading to difficult CSS customation) [1]

  (+) add an "outline" theme (such as the <ui-button>)
  (+) slot for an aside content (eg. you can add an <ui-icon> within it)

  (?) The "caveat" term is a word I personally use for a long time in frontend.
      I always thought it came out from a <caveat> component from the
      "bootstrap" early days.
      However with proper research, I couldn't find anything like it
      (mandela effect?). The name's great for what it serves though.

  ==============================================================

  # Advanced example "side icon w/ caveat content"

  ```template
    <ui-caveat
      :variant="UI.VARIANT.LIGHT"
      :aside-variant="UI.VARIANT.INFO"
      :size="UI.SIZE.SMALL"
    >
      <template #aside>
        <ui-icon name="face" />
      </template>
      <template #default>
        ...
      </template>
    </ui-caveat>
  ```

  @link https://bootstrap-vue-next.github.io/bootstrap-vue-next/docs/components/card.html [1]
-->
<template>
  <div
    :class="[
      'ui-caveat',
      `ui-caveat--${ variant }`,
      `ui-caveat--${ size }`,
      outline ? 'ui-caveat--outline' : '',
      'card',
    ]"
  >
    <div
      v-if="$slots.aside"
      :class="[
        'ui-caveat__aside',
        `ui-caveat__aside--${ proxyAsideVariant }`,
        asideClass
      ]"
    >
      <slot name="aside" />
    </div>

    <div
      :class="[
        'ui-caveat__body',
        bodyClass
      ]"
    >
      <slot name="default" />
    </div>

  </div>
</template>

<style lang="sass">
$color-modifier: 30%

.ui-caveat
  display: flex
  flex-direction: initial // reset ".card" flex rules
  position: relative

  // Body
  &__body
    flex-grow: 1

  // Aside column
  &__aside
    border-top-left-radius: inherit
    border-bottom-left-radius: inherit

    @each $theme, $value in $palette-semantic

      &--#{$theme}

        @if ($theme == light)
          color: $white
          background-color: darken($value, $color-modifier)

        @else if ($theme == primary)
          /// @XXX GHS 2024 Wine Berry not suitable with brightened adjustment
          /// (workaround: prevent adjustment)
          color: $white
          background-color: $primary

        @else
          color: $white
          background-color: brighten($value, $color-modifier)

    // micro managing inner icon placement
    .ui-icon
      position: relative
      line-height: 0


  /// Normal display
  border-color: map-get($palette-semantic, 'light')
  color: $white

  @each $theme, $value in $palette-semantic

    &--#{$theme}
      // (using `background` here to override "sass/base/bootstrap.scss" .card
      // style)
      background: $value

  &--light
    color: $gray-40

  /// Outline display
  @each $theme, $value in $palette-semantic

    &--outline#{&}--#{$theme}
      background-color: $white
      color: $value
      box-shadow: 0 0 0 2px brighten($value, $color-modifier)

  /// Sizing
  &--sm
    font-size: 90%
  &--sm &
    &__body
      padding: space-x(.75) space-x(1.25)
    &__aside
      padding: space-x(.75)
    &__aside .ui-icon
      font-size: $icon-default-size - 4px

  &--md &
    &__body
      padding: space-x(1.5) space-x(2.25)
    &__aside
      padding: space-x(1.5)
    &__aside .ui-icon
      font-size: $icon-default-size

  &--lg
    font-size: 110%
  &--lg &
    &__body
      padding: space-x(2) space-x(3)
    &__aside
      padding: space-x(2)
    &__aside .ui-icon
      font-size: $icon-default-size + 2px

</style>

<script lang="ts">
import type { SlotsType } from 'vue'
import { emptyCoalescing } from '~/utils/lib/core/prelude'
import type { SIZE, VARIANT } from '~/utils/lib/layout/ui'
export default defineComponent({
  props: {
    variant: {
      type: String as PropType<VARIANT>,
      required: false,
      default: 'light' satisfies VARIANT
    },
    size: {
      type: String as PropType<SIZE>,
      required: false,
      default: 'md' satisfies SIZE
    },
    outline: {
      type: Boolean,
      required: false,
      default: false
    },
    asideVariant: {
      type: String,
      required: false,
      default: ''
    },
    bodyClass: {
      type: [ String, Array ],
      required: false,
      default: null
    },
    asideClass: {
      type: [ String, Array ],
      required: false,
      default: null
    },
  },
  slots: Object as SlotsType<{
    'default': {},
    // @XXX "vue-language-tools" Template conditional $slots not working
    //    ↳ monkey-patch: using optional typing property
    'aside'?: {},
  }>,
  computed: {
    proxyAsideVariant() {
      return emptyCoalescing(this.asideVariant, this.variant)
    }
  }
})
</script>
